import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { fr } from "./fr";
import { en } from "./en";

export const SUPPORTED_LOCALES = ["fr", "en"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";
export const STORAGE_KEY = "velvet_hub.locale";

// Détecte la locale initiale (avant le boot React) :
//   1. localStorage : choix manuel persistant. Priorité absolue.
//   2. Sinon, on bootstrap avec la locale du navigateur (Accept-Language)
//      pour avoir un texte cohérent dès la 1ère frame, le temps que
//      `useLocale` finisse l'appel /api/i18n/detect-locale et override
//      éventuellement avec la décision GeoIP côté serveur.
function getInitialLocale(): Locale {
  if (typeof window === "undefined") return DEFAULT_LOCALE;

  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && (SUPPORTED_LOCALES as readonly string[]).includes(saved)) {
      return saved as Locale;
    }
  } catch {
    /* no-op : Safari privé peut throw */
  }

  const navLang = window.navigator?.language?.toLowerCase() ?? "";
  if (navLang.startsWith("fr")) return "fr";
  return DEFAULT_LOCALE;
}

void i18n.use(initReactI18next).init({
  resources: {
    fr: { translation: fr },
    en: { translation: en },
  },
  lng: getInitialLocale(),
  fallbackLng: DEFAULT_LOCALE,
  interpolation: {
    escapeValue: false, // React déjà sécurise
  },
  // Clés manquantes → on log en dev pour ne rien rater quand on ajoute
  // des entrées dans une langue mais pas l'autre.
  saveMissing: import.meta.env.DEV,
  missingKeyHandler: (lngs, _ns, key) => {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`[i18n] missing key for ${lngs.join("/")}: ${key}`);
    }
  },
});

export default i18n;
